import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LogoComponent } from '../../../components/logo/logo.component';
import { NavigationService } from '../../../services/navigation.service';
import { RouterLink, RouterLinkActive } from '@angular/router';

export interface SuperAdminNavItem {
  label: string;
  path: string;
  icon: string;
}

/**
 * SuperAdminSideNavComponent renders the side navigation for the Super Admin area, managing collapsed state and mobile navigation visibility.
 * @component
 * @selector app-super-admin-side-nav
 * @example
 *   <app-super-admin-side-nav></app-super-admin-side-nav>
 * @input {none} none - No public @Input() properties. The component reads its state from the injected NavigationService observables.
 * @output {none} none - No public @Output() events are emitted. Interaction is propagated via the NavigationService.
 *
 * Description:
 *   This standalone Angular component provides a responsive side navigation used by super administrators. It subscribes to NavigationService observables to reflect and control:
 *     - sidebar collapsed/expanded state (isCollapsed)
 *     - mobile navigation open/closed state (isMobileNavOpen)
 *
 * Key Methods:
 *   - toggleCollapse(): Toggles the local isCollapsed flag and notifies the NavigationService via setSidebarCollapsed(collapsed).
 *   - closeMobileNav(): Requests the NavigationService to close mobile navigation by calling setMobileNavOpen(false).
 *   - onMenuLinkMouseEnter(event: MouseEvent): Placeholder for menu link hover handling (e.g., tooltip or preview behavior).
 *
 * Lifecycle Hooks:
 *   - constructor(navigationService: NavigationService): Subscribes to isMobileNavOpen$ to keep isMobileNavOpen in sync with NavigationService.
 *   - ngOnInit(): Subscribes to isSidebarCollapsed$ to initialize and react to collapsed state changes from NavigationService.
 *   - ngOnDestroy(): Present for cleanup; current implementation has no explicit unsubscribe logic here (subscriptions rely on service-managed observables). Consider adding explicit unsubscription if observables are not completed by the service.
 *
 * Notes:
 *   - State and cross-component coordination are done exclusively through the injected NavigationService, not via inputs/outputs.
 *   - Template and styling are provided in separate files: super-admin-side-nav.component.html / .scss.
 */
@Component({
  selector: 'app-super-admin-side-nav',
  standalone: true,
  imports: [CommonModule, LogoComponent, RouterLink, RouterLinkActive],
  templateUrl: './super-admin-side-nav.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./super-admin-side-nav.component.scss'],
})
export class SuperAdminSideNavComponent implements OnInit {
  isCollapsed = false;
  isMobileNavOpen = false;

  /**
   * Companies is the hub: location settings (lanes, tunnel, Way), impersonation,
   * bulk import and per-company audit log all live under a company, so they are reached by picking one rather than
   * from their own top-level entries.
   */
  readonly primaryNav: SuperAdminNavItem[] = [
    {
      label: 'Orders',
      path: '/admin/dashboard',
      icon: '/assets/images/sidebar/orders-icon-new.svg',
    },
    {
      label: 'Customers',
      path: '/admin/customers',
      icon: '/assets/images/sidebar/customers-icon.svg',
    },
    {
      label: 'Campaigns',
      path: '/admin/campaigns',
      icon: '/assets/images/sidebar/analytics-icon.svg',
    },
    {
      label: 'Companies',
      path: '/admin/companies',
      icon: '/assets/images/sidebar/building-icon.svg',
    },
  ];

  /** Cross-company views that have no single company to hang off. */
  readonly systemNav: SuperAdminNavItem[] = [
    { label: 'Audit Log', path: '/admin/audit-log', icon: '/assets/images/sidebar/crm-icon.svg' },
    {
      label: 'Service Status',
      path: '/admin/status',
      icon: '/assets/images/sidebar/employee-icon.svg',
    },
    {
      label: 'Tenancy Reconcile',
      path: '/admin/tenancy-reconcile',
      icon: '/assets/images/sidebar/reports-icon.svg',
    },
    {
      label: 'Feature Flags',
      path: '/admin/feature-flags',
      icon: '/assets/images/sidebar/settings-icon.svg',
    },
  ];

  /**
   * Look-ups across every shop for support work. Plate Lookup today; Way+ checks
   * join this group later.
   */
  readonly diagnosticsNav: SuperAdminNavItem[] = [
    {
      label: 'Plate Lookup',
      path: '/admin/plate-lookup',
      icon: '/assets/images/sidebar/fleet-icon.svg',
    },
  ];

  /** Desktop icon-rail collapse only — portrait hamburger drawer is always expanded. */
  get isNavCollapsed(): boolean {
    return this.isCollapsed && !this.isMobileNavOpen;
  }

  constructor(private navigationService: NavigationService) {
    this.navigationService.isMobileNavOpen$.subscribe(isOpen => {
      this.isMobileNavOpen = isOpen;
    });
  }

  ngOnInit(): void {
    // Initialize sidebar state
    this.navigationService.isSidebarCollapsed$.subscribe(collapsed => {
      this.isCollapsed = collapsed;
    });
  }

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.navigationService.setSidebarCollapsed(this.isCollapsed);
  }

  closeMobileNav() {
    this.navigationService.setMobileNavOpen(false);
  }

  onMenuLinkMouseEnter(event: MouseEvent): void {
    // Tooltip handling if needed
  }
}
